import React, { useState, useEffect, useCallback } from 'react';
import { 
  Home, 
  Users, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Search,
  Heart,
  Shield,
  Activity,
  PieChart,
  BarChart3,
  UserCheck,
  UserX,
  Building,
  Percent,
  Clock,
  ArrowRight
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import api from '@/lib/api';
import AsyncSearchSelect from '@/components/common/AsyncSearchSelect';
import { participantLabel, PARTICIPANT_SEARCH_HINT } from '@/components/common/pickerLabels';

const SILIntelligencePage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [costDashboard, setCostDashboard] = useState(null);
  const [ratioDashboard, setRatioDashboard] = useState(null);
  const [compatibilityAlerts, setCompatibilityAlerts] = useState(null);
  const [houses, setHouses] = useState([]);
  
  // Compatibility check dialog
  const [showCompatibilityDialog, setShowCompatibilityDialog] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [selectedHouse, setSelectedHouse] = useState('');
  const [compatibilityResult, setCompatibilityResult] = useState(null);
  const [checkingCompatibility, setCheckingCompatibility] = useState(false);
  
  // House matches dialog
  const [showMatchesDialog, setShowMatchesDialog] = useState(false);
  const [matchResults, setMatchResults] = useState(null);
  const [findingMatches, setFindingMatches] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, costRes, ratioRes, alertsRes, housesRes] = await Promise.all([
        api.get('/sil-intelligence/dashboard'),
        api.get('/sil-costs/dashboard'),
        api.get('/sil-costs/ratio-compliance'),
        api.get('/sil-intelligence/compatibility/alerts'),
        api.get('/houses?status=ACTIVE&limit=100')
      ]);
      
      setDashboard(dashRes.data?.data);
      setCostDashboard(costRes.data?.data);
      setRatioDashboard(ratioRes.data?.data);
      setCompatibilityAlerts(alertsRes.data?.data);
      setHouses(housesRes.data?.items || housesRes.data?.data || []);
    } catch (err) {
      notify.error(err, 'SIL_DATA_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value || 0);
  };

  const getCompatibilityColor = (level) => {
    const colors = {
      'EXCELLENT': 'bg-green-500',
      'GOOD': 'bg-blue-500',
      'MODERATE': 'bg-yellow-500',
      'POOR': 'bg-orange-500',
      'INCOMPATIBLE': 'bg-red-500'
    };
    return colors[level] || 'bg-gray-500';
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600';
    if (score >= 70) return 'text-blue-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const checkCompatibility = async () => {
    if (!selectedParticipant || !selectedHouse) {
      notify.warn('SIL_VALIDATION_PARTICIPANT_HOUSE');
      return;
    }
    
    try {
      setCheckingCompatibility(true);
      const res = await api.get(`/sil-intelligence/compatibility/check?participant_id=${selectedParticipant}&house_id=${selectedHouse}`);
      setCompatibilityResult(res.data?.data);
    } catch (err) {
      notify.error(err, 'SIL_COMPATIBILITY_FAILED');
    } finally {
      setCheckingCompatibility(false);
    }
  };

  const findMatches = async () => {
    if (!selectedParticipant) {
      notify.warn('SIL_VALIDATION_PARTICIPANT');
      return;
    }
    
    try {
      setFindingMatches(true);
      const res = await api.get(`/sil-intelligence/compatibility/find-matches?participant_id=${selectedParticipant}`);
      setMatchResults(res.data?.data);
      setShowMatchesDialog(true);
    } catch (err) {
      notify.error(err, 'SIL_MATCH_FAILED');
    } finally {
      setFindingMatches(false);
    }
  };

  const SummaryCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <Card className={`border-l-4 ${color}`} data-testid={`summary-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-full ${trend === 'down' ? 'bg-red-100' : 'bg-gray-100'}`}>
            <Icon className={`w-6 h-6 ${trend === 'down' ? 'text-red-600' : 'text-gray-600'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="sil-intelligence-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Home className="w-7 h-7 text-blue-600" />
            SIL / Accommodation Intelligence
          </h1>
          <p className="text-gray-600 mt-1">
            House funding, staff ratios, participant compatibility
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setShowCompatibilityDialog(true)} data-testid="check-compatibility-btn">
            <Search className="w-4 h-4 mr-2" />
            Check Compatibility
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <SummaryCard
            title="Total Houses"
            value={dashboard.summary.total_houses}
            subtitle={`${dashboard.summary.occupancy_rate}% occupancy`}
            icon={Building}
            color="border-blue-500"
          />
          <SummaryCard
            title="Occupied / Capacity"
            value={`${dashboard.summary.total_occupied} / ${dashboard.summary.total_capacity}`}
            subtitle="Residents housed"
            icon={Users}
            color="border-green-500"
          />
          <SummaryCard
            title="Ratio Compliant"
            value={`${dashboard.summary.ratio_compliant} / ${dashboard.summary.ratio_compliant + dashboard.summary.ratio_non_compliant}`}
            subtitle={`${dashboard.ratio_compliance.compliance_rate}% compliance`}
            icon={Shield}
            color={dashboard.summary.ratio_non_compliant > 0 ? "border-yellow-500" : "border-green-500"}
            trend={dashboard.summary.ratio_non_compliant > 0 ? 'down' : null}
          />
          <SummaryCard
            title="Compatibility Alerts"
            value={dashboard.summary.compatibility_alerts}
            subtitle={`${dashboard.summary.houses_with_compatibility_issues} houses affected`}
            icon={AlertTriangle}
            color="border-red-500"
            trend={dashboard.summary.compatibility_alerts > 0 ? 'down' : null}
          />
        </div>
      )}

      {/* Financial Summary */}
      {dashboard && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Financial Overview (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Total Staff Cost</p>
                <p className="text-xl font-bold">{formatCurrency(dashboard.financial.total_staff_cost)}</p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Total Revenue</p>
                <p className="text-xl font-bold">{formatCurrency(dashboard.financial.total_revenue)}</p>
              </div>
              <div className={`p-4 rounded-lg ${dashboard.financial.total_margin >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="text-sm text-gray-500">Margin</p>
                <p className={`text-xl font-bold ${dashboard.financial.total_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                  {formatCurrency(dashboard.financial.total_margin)}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">Profitability</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-green-600 font-bold">{dashboard.summary.houses_profitable}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-red-600 font-bold">{dashboard.summary.houses_loss_making}</span>
                  <span className="text-xs text-gray-500">(profit/loss)</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs defaultValue="costs" className="space-y-4">
        <TabsList>
          <TabsTrigger value="costs" data-testid="tab-costs">
            <DollarSign className="w-4 h-4 mr-2" />
            House Costs
          </TabsTrigger>
          <TabsTrigger value="ratios" data-testid="tab-ratios">
            <Percent className="w-4 h-4 mr-2" />
            Staff Ratios
          </TabsTrigger>
          <TabsTrigger value="compatibility" data-testid="tab-compatibility">
            <Heart className="w-4 h-4 mr-2" />
            Compatibility
          </TabsTrigger>
        </TabsList>

        {/* House Costs Tab */}
        <TabsContent value="costs">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">House Cost Analysis</CardTitle>
              <CardDescription>Financial performance by house</CardDescription>
            </CardHeader>
            <CardContent>
              {costDashboard?.houses?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Building className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No house cost data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {costDashboard?.houses?.map((house, i) => (
                    <div 
                      key={house.house_id}
                      className={`p-4 border rounded-lg ${
                        house.status === 'PROFITABLE' ? 'border-green-200 bg-green-50' :
                        house.status === 'LOSS' ? 'border-red-200 bg-red-50' :
                        'border-gray-200 bg-gray-50'
                      }`}
                      data-testid={`house-cost-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{house.house_name}</p>
                          <p className="text-sm text-gray-500">
                            {house.total_residents} residents &bull; {house.total_shifts} shifts &bull; {house.total_hours}h
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge className={house.status === 'PROFITABLE' ? 'bg-green-500' : house.status === 'LOSS' ? 'bg-red-500' : 'bg-gray-500'}>
                            {house.status}
                          </Badge>
                          <p className={`text-lg font-bold mt-1 ${house.margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(house.margin)}
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 mt-3 text-sm">
                        <div>
                          <p className="text-gray-500">Staff Cost</p>
                          <p className="font-medium">{formatCurrency(house.total_staff_cost)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Revenue</p>
                          <p className="font-medium">{formatCurrency(house.total_service_revenue)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Cost/Resident</p>
                          <p className="font-medium">{formatCurrency(house.cost_per_resident)}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Staff Ratio</p>
                          <p className="font-medium">{house.staff_ratio}h/day</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Ratios Tab */}
        <TabsContent value="ratios">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">NDIS Staff Ratio Compliance</CardTitle>
              <CardDescription>
                {ratioDashboard?.summary?.compliance_rate || 0}% overall compliance | Gap: {ratioDashboard?.summary?.total_gap_hours_per_week || 0}h/week
              </CardDescription>
            </CardHeader>
            <CardContent>
              {ratioDashboard?.houses?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No ratio data available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ratioDashboard?.houses?.map((house, i) => (
                    <div 
                      key={house.house_id}
                      className={`p-4 border rounded-lg ${house.compliant ? 'border-green-200' : 'border-red-200'}`}
                      data-testid={`house-ratio-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{house.house_name}</p>
                          <p className="text-sm text-gray-500">{house.total_residents} residents</p>
                        </div>
                        <Badge className={house.compliant ? 'bg-green-500' : 'bg-red-500'}>
                          {house.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
                        </Badge>
                      </div>
                      <div className="mt-3">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span>Required: {house.ndis_required_ratio}h/day</span>
                          <span>Actual: {house.actual_ratio}h/day</span>
                        </div>
                        <Progress 
                          value={Math.min(100, (house.actual_ratio / house.ndis_required_ratio) * 100)} 
                          className={house.compliant ? 'bg-green-100' : 'bg-red-100'}
                        />
                        {!house.compliant && (
                          <p className="text-sm text-red-600 mt-2">
                            Gap: {house.gap_hours_per_week}h/week additional staffing needed
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compatibility Tab */}
        <TabsContent value="compatibility">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Compatibility Alerts
              </CardTitle>
              <CardDescription>
                {compatibilityAlerts?.total_alerts || 0} alerts across {compatibilityAlerts?.houses_with_issues || 0} houses
              </CardDescription>
            </CardHeader>
            <CardContent>
              {compatibilityAlerts?.alerts?.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <p>No compatibility issues detected</p>
                  <p className="text-sm">All house residents are compatible</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {compatibilityAlerts?.alerts?.map((alert, i) => (
                    <div 
                      key={i}
                      className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
                      data-testid={`compatibility-alert-${i}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{alert.house_name}</p>
                        <Badge variant="outline" className="border-yellow-500 text-yellow-700">
                          Harmony: {alert.harmony_score}%
                        </Badge>
                      </div>
                      <p className="text-sm text-yellow-800">{alert.alert}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Compatibility Check Dialog */}
      <Dialog open={showCompatibilityDialog} onOpenChange={setShowCompatibilityDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Check Placement Compatibility</DialogTitle>
            <DialogDescription>
              Select a participant and house to check compatibility
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Participant</label>
                <AsyncSearchSelect
                  endpoint="/participants"
                  params={{ status: 'ACTIVE' }}
                  value={selectedParticipant}
                  onChange={(v) => setSelectedParticipant(v)}
                  getOptionLabel={participantLabel}
                  placeholder="Select participant"
                  searchPlaceholder={PARTICIPANT_SEARCH_HINT}
                  emptyText="No participants match that search"
                  testId="select-participant"
                />
              </div>
              <div>
                <label className="text-sm font-medium">House</label>
                <Select value={selectedHouse} onValueChange={setSelectedHouse}>
                  <SelectTrigger data-testid="select-house">
                    <SelectValue placeholder="Select house" />
                  </SelectTrigger>
                  <SelectContent>
                    {houses.map(h => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={checkCompatibility} 
                disabled={checkingCompatibility || !selectedParticipant || !selectedHouse}
                data-testid="run-check-btn"
              >
                {checkingCompatibility ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                Check Compatibility
              </Button>
              <Button 
                variant="outline"
                onClick={findMatches} 
                disabled={findingMatches || !selectedParticipant}
                data-testid="find-matches-btn"
              >
                {findingMatches ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Heart className="w-4 h-4 mr-2" />}
                Find Best Matches
              </Button>
            </div>
            
            {compatibilityResult && (
              <div className="border rounded-lg p-4 mt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="font-medium">{compatibilityResult.participant_name}</p>
                    <p className="text-sm text-gray-500">→ {compatibilityResult.house_name}</p>
                  </div>
                  <div className="text-right">
                    <Badge className={getCompatibilityColor(compatibilityResult.compatibility_level)}>
                      {compatibilityResult.compatibility_level}
                    </Badge>
                    <p className={`text-2xl font-bold mt-1 ${getScoreColor(compatibilityResult.overall_score)}`}>
                      {compatibilityResult.overall_score}%
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 mb-4">
                  {compatibilityResult.can_place ? (
                    <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Can Place</Badge>
                  ) : (
                    <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Cannot Place</Badge>
                  )}
                </div>
                
                {/* Factor breakdown */}
                <div className="space-y-2 mb-4">
                  <p className="text-sm font-medium">Factor Analysis:</p>
                  {compatibilityResult.factors?.map((factor, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="capitalize">{factor.factor.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <Progress value={factor.score} className="w-24 h-2" />
                        <span className={getScoreColor(factor.score)}>{factor.score}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Risks */}
                {compatibilityResult.risks?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-red-600 mb-1">Risks:</p>
                    <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                      {compatibilityResult.risks.map((risk, i) => (
                        <li key={i}>{risk}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Recommendations */}
                {compatibilityResult.recommendations?.length > 0 && (
                  <div>
                    <p className="text-sm font-medium text-blue-600 mb-1">Recommendations:</p>
                    <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
                      {compatibilityResult.recommendations.map((rec, i) => (
                        <li key={i}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompatibilityDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* House Matches Dialog */}
      <Dialog open={showMatchesDialog} onOpenChange={setShowMatchesDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>House Recommendations</DialogTitle>
            <DialogDescription>
              {matchResults?.total_matches || 0} compatible houses found
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {matchResults?.matches?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Home className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No compatible houses found</p>
              </div>
            ) : (
              matchResults?.matches?.map((match, i) => (
                <div 
                  key={match.house_id}
                  className="p-4 border rounded-lg hover:bg-gray-50"
                  data-testid={`match-${i}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium">{match.house_name}</p>
                      <p className="text-sm text-gray-500">{match.address}</p>
                    </div>
                    <div className="text-right">
                      <Badge className={getCompatibilityColor(match.compatibility_level)}>
                        {match.compatibility_level}
                      </Badge>
                      <p className={`text-xl font-bold ${getScoreColor(match.compatibility_score)}`}>
                        {match.compatibility_score}%
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-2">
                    <span>{match.property_type}</span>
                    <span>{match.current_residents}/{match.capacity} occupied</span>
                    <span className="text-green-600">{match.available_spots} spots available</span>
                  </div>
                  
                  <div className="flex gap-4">
                    {match.key_benefits?.length > 0 && (
                      <div className="flex-1">
                        <p className="text-xs text-green-600 font-medium">Benefits:</p>
                        <div className="flex flex-wrap gap-1">
                          {match.key_benefits.map((b, bi) => (
                            <Badge key={bi} variant="outline" className="text-xs border-green-300 text-green-700">{b}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {match.potential_concerns?.length > 0 && (
                      <div className="flex-1">
                        <p className="text-xs text-yellow-600 font-medium">Concerns:</p>
                        <div className="flex flex-wrap gap-1">
                          {match.potential_concerns.map((c, ci) => (
                            <Badge key={ci} variant="outline" className="text-xs border-yellow-300 text-yellow-700">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMatchesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SILIntelligencePage;
